import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Inbox, Loader2, Search, UserCheck } from 'lucide-react';
import { useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Pill, StatusPill } from '@/components/panels/SectionCard';
import { getErrorMessage } from '@/lib/errors';
import {
  adminSupportApi,
  priorityTone,
  statusTone,
  supportKeys,
  type TicketPriority,
  type TicketStatus,
} from '@/lib/support-api';
import { humanDuration, relativeTime } from '@/modules/monitoring/format';
import { ReplyBox, TicketThread } from '@/modules/support/TicketThread';

const FILTERS: Array<{ label: string; statuses: TicketStatus[] }> = [
  { label: 'Needs a reply', statuses: ['open', 'awaiting_support'] },
  { label: 'Waiting on customer', statuses: ['awaiting_customer'] },
  { label: 'Resolved', statuses: ['resolved'] },
  { label: 'Closed', statuses: ['closed'] },
  { label: 'Everything', statuses: [] },
];

/**
 * The support queue, across every tenant.
 *
 * ## Defaults to "needs a reply", not to everything
 *
 * A queue that opens on every ticket ever filed is a queue somebody has to filter before they can
 * start, every single time. The default is the work list.
 *
 * ## Ordered by who has waited longest, with unanswered first
 *
 * The API decides this, not the table. Priority is shown and filterable but does not lead the
 * sort: ordering by priority starves everything below the top band whenever the top band is
 * non-empty, and the low-priority ticket nobody answered for nine days is the one that loses the
 * customer.
 */
export function AdminSupportPage() {
  const [filter, setFilter] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);

  const statuses = FILTERS[filter]?.statuses ?? [];
  const filterKey = `${statuses.join(',')}|${search}|${page}`;

  const queue = useQuery({
    queryKey: supportKeys.queue(filterKey),
    queryFn: () => adminSupportApi.queue({ status: statuses, q: search, page }),
    // Refreshed while somebody has the queue open, so a new ticket appears without a reload.
    refetchInterval: 30000,
  });

  if (openTicketId !== null) {
    return <StaffTicketDetail ticketId={openTicketId} onBack={() => setOpenTicketId(null)} />;
  }

  const counts = queue.data?.counts;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Support queue</h1>
        <p className="text-sm text-muted-foreground">
          Every customer's tickets. Longest wait first, unanswered above everything.
        </p>
      </div>

      {counts !== undefined && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Figure
            label="Never answered"
            value={counts.unanswered}
            alarming={counts.unanswered > 0}
            hint="nobody has replied at all"
          />
          <Figure label="Needs a reply" value={counts.open + counts.awaiting_support} />
          <Figure label="Waiting on customer" value={counts.awaiting_customer} />
          <Figure label="Resolved" value={counts.resolved} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((entry, index) => (
          <button
            key={entry.label}
            type="button"
            onClick={() => {
              setFilter(index);
              setPage(1);
            }}
            className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
              filter === index ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-muted/50'
            }`}
          >
            {entry.label}
          </button>
        ))}

        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-56 pl-9"
            aria-label="Search tickets by reference or subject"
          />
        </div>
      </div>

      {queue.isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading the queue…
        </div>
      )}

      {queue.isError && <Alert variant="danger">{getErrorMessage(queue.error)}</Alert>}

      {queue.data !== undefined && queue.data.items.length === 0 && (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <span className="inline-flex size-12 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-500">
            <Inbox className="size-6" />
          </span>
          <p className="font-medium">Nothing here</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            {statuses.length === 0
              ? 'No tickets have been opened yet.'
              : 'No tickets match this filter.'}
          </p>
        </Card>
      )}

      {queue.data !== undefined && queue.data.items.length > 0 && (
        <div className="space-y-3">
          {queue.data.items.map((ticket) => (
            <Card
              key={ticket.id}
              className="cursor-pointer p-4 transition-colors hover:border-primary/40"
              onClick={() => setOpenTicketId(ticket.id)}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-muted-foreground">
                      {ticket.reference}
                    </span>
                    <StatusPill label={ticket.status_label} tone={statusTone(ticket.status)} />
                    {ticket.priority !== 'normal' && (
                      <Pill tone={priorityTone(ticket.priority)}>{ticket.priority}</Pill>
                    )}
                    {!ticket.has_been_answered && <Pill tone="danger">never answered</Pill>}
                  </div>
                  <p className="mt-1 truncate font-medium">{ticket.subject}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {ticket.opened_by_name} · last message {relativeTime(ticket.last_message_at)}
                    {ticket.assigned_to_name != null && ` · ${ticket.assigned_to_name}`}
                  </p>
                </div>

                {/* Only when the customer is the one waiting. A number that counted our own side
                    of the conversation would rank tickets we already answered to the top. */}
                {ticket.waiting_seconds != null && (
                  <div className="shrink-0 text-right">
                    <p
                      className={`text-sm font-semibold tabular-nums ${
                        ticket.waiting_seconds > 86400 ? 'text-danger' : ''
                      }`}
                    >
                      {humanDuration(ticket.waiting_seconds)}
                    </p>
                    <p className="text-xs text-muted-foreground">waiting</p>
                  </div>
                )}
              </div>
            </Card>
          ))}

          {queue.data.total > queue.data.per_page && (
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                {queue.data.total} tickets · page {page} of{' '}
                {Math.ceil(queue.data.total / queue.data.per_page)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page * queue.data.per_page >= queue.data.total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StaffTicketDetail({ ticketId, onBack }: { ticketId: string; onBack: () => void }) {
  const queryClient = useQueryClient();

  const thread = useQuery({
    queryKey: supportKeys.staffThread(ticketId),
    queryFn: () => adminSupportApi.thread(ticketId),
    refetchInterval: 20000,
  });

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: supportKeys.staffThread(ticketId) }),
      queryClient.invalidateQueries({ queryKey: ['support', 'queue'] }),
    ]);

  const reply = useMutation({
    mutationFn: ({ body, internal }: { body: string; internal: boolean }) =>
      adminSupportApi.reply(ticketId, body, internal),
    onSuccess: refresh,
  });

  const attach = useMutation({
    mutationFn: (file: File) => adminSupportApi.attach(ticketId, file),
    onSuccess: refresh,
  });

  const setStatus = useMutation({
    mutationFn: (status: TicketStatus) => adminSupportApi.changeStatus(ticketId, status),
    onSuccess: refresh,
  });

  const setMeta = useMutation({
    mutationFn: (body: { priority?: TicketPriority; assign_to_me?: boolean; unassign?: boolean }) =>
      adminSupportApi.updateMeta(ticketId, body),
    onSuccess: refresh,
  });

  if (thread.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Loading the ticket…
      </div>
    );
  }

  if (thread.isError) {
    return (
      <div className="space-y-4">
        <Alert variant="danger">{getErrorMessage(thread.error)}</Alert>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 size-4" />
          Back to the queue
        </Button>
      </div>
    );
  }

  if (thread.data === undefined) {
    return null;
  }

  const { ticket, messages, attachments } = thread.data;
  const busy = setStatus.isPending || setMeta.isPending;

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ArrowLeft className="mr-2 size-4" />
        Back to the queue
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">{ticket.reference}</span>
            <StatusPill label={ticket.status_label} tone={statusTone(ticket.status)} />
            <Pill tone={priorityTone(ticket.priority)}>{ticket.priority}</Pill>
          </div>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">{ticket.subject}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Opened by {ticket.opened_by_name} · {relativeTime(ticket.created_at)}
            {ticket.first_response_at == null && ' · never answered'}
          </p>
        </div>
      </div>

      {(reply.isError || attach.isError || setStatus.isError || setMeta.isError) && (
        <Alert variant="danger">
          {getErrorMessage(reply.error ?? attach.error ?? setStatus.error ?? setMeta.error)}
        </Alert>
      )}

      <Card className="flex flex-wrap items-center gap-2 p-4">
        <span className="mr-1 text-sm text-muted-foreground">Status</span>
        {(['awaiting_customer', 'awaiting_support', 'resolved', 'closed'] as TicketStatus[]).map(
          (status) => (
            <Button
              key={status}
              variant={ticket.status === status ? 'default' : 'outline'}
              size="sm"
              disabled={busy}
              onClick={() => setStatus.mutate(status)}
            >
              {status.replace(/_/g, ' ')}
            </Button>
          ),
        )}

        <span className="ml-4 mr-1 text-sm text-muted-foreground">Priority</span>
        <select
          value={ticket.priority}
          disabled={busy}
          onChange={(e) => setMeta.mutate({ priority: e.target.value as TicketPriority })}
          className="rounded-md border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary/50"
        >
          <option value="low">low</option>
          <option value="normal">normal</option>
          <option value="high">high</option>
          <option value="urgent">urgent</option>
        </select>

        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          disabled={busy}
          onClick={() =>
            setMeta.mutate(
              ticket.assigned_to_user_id == null ? { assign_to_me: true } : { unassign: true },
            )
          }
        >
          <UserCheck className="mr-2 size-4" />
          {ticket.assigned_to_user_id == null
            ? 'Assign to me'
            : `Unassign from ${ticket.assigned_to_name ?? 'someone'}`}
        </Button>
      </Card>

      <TicketThread
        messages={messages}
        attachments={attachments}
        attachmentPath={(attachmentId) => adminSupportApi.attachmentPath(ticket.id, attachmentId)}
      />

      <Card className="p-0">
        <ReplyBox
          allowInternal
          onSend={async (body, internal) => {
            await reply.mutateAsync({ body, internal });
          }}
          onAttach={async (file) => {
            await attach.mutateAsync(file);
          }}
          hint="Replying moves this to 'awaiting customer'. A note changes nothing."
        />
      </Card>
    </div>
  );
}

function Figure({
  label,
  value,
  hint,
  alarming = false,
}: {
  label: string;
  value: number;
  hint?: string;
  alarming?: boolean;
}) {
  return (
    <Card className="p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p
        className={`text-2xl font-semibold tabular-nums ${alarming ? 'text-danger' : ''}`}
      >
        {value}
      </p>
      {hint !== undefined && <p className="text-xs text-muted-foreground">{hint}</p>}
    </Card>
  );
}
